(function () {
  'use strict';

  function esc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  class FbtBundle extends HTMLElement {
    constructor() {
      super();
      this.currency = this.dataset.currency || 'BRL';
      this.locale = this.dataset.locale || 'pt-BR';
      this.discountMode = this.dataset.discountMode || 'none';
      this.flatDiscount = Number(this.dataset.flatDiscount) || 0;
      this.flatCode = String(this.dataset.flatCode || '').trim();
      this.minimumRecommendations = Math.max(3, Number(this.dataset.limit) || 3);
      this.emptyLabel = this.dataset.emptyLabel || '';
      this.selectedLabel = this.dataset.selectedLabel || '';
      this.comboPriceLabel = this.dataset.comboPriceLabel || 'No combo';
      this.comboDiscountLabel = this.dataset.comboDiscountLabel || '[discount]% OFF extra';
      this.discountNote = this.dataset.discountNote || '';
      this.tiers = [
        { count: 1, percent: Number(this.dataset.tier1Discount) || 0, code: String(this.dataset.tier1Code || '').trim() },
        { count: 2, percent: Number(this.dataset.tier2Discount) || 0, code: String(this.dataset.tier2Code || '').trim() },
        { count: 3, percent: Number(this.dataset.tier3Discount) || 0, code: String(this.dataset.tier3Code || '').trim() },
      ];
      try {
        this.formatter = new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currency });
      } catch (error) {
        this.formatter = { format: function (value) { return 'R$ ' + value.toFixed(2); } };
      }
    }

    connectedCallback() {
      this.totalEl = this.querySelector('.fbt__total');
      this.addBtn = this.querySelector('.fbt__add');
      this.buttonLabel = this.querySelector('.fbt__button-label');
      this.buttonSeparator = this.querySelector('.fbt__button-separator');
      this.statusEl = this.querySelector('.fbt__tier-status');
      this.list = this.querySelector('.fbt__list');
      this.kitContext = this.readKitContext();
      this.kitComponentIds = new Set((this.kitContext.componentProductIds || []).map(String));
      this.currentProductIsKit = this.isKitTitle(this.kitContext.title);
      this.kitDescriptionTokens = this.significantTokens(this.kitContext.description);

      this.sanitizeExistingRows();

      var manualCompanions = this.querySelectorAll('.fbt__check:not(:disabled)').length;
      if (this.dataset.dynamic === 'true' || manualCompanions < this.minimumRecommendations) {
        this.loadRecommendations();
      } else {
        this.initRows();
      }
    }

    rootPath() {
      return window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    }

    parseProducts(text) {
      var container = document.createElement('div');
      container.innerHTML = text;
      var json = container.querySelector('[data-fbt-products]');
      if (!json) return [];
      try {
        return JSON.parse(json.textContent.trim());
      } catch (error) {
        return [];
      }
    }

    fetchProducts(url) {
      return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) { return response.text(); })
        .then(this.parseProducts.bind(this))
        .catch(function () { return []; });
    }

    recommendationUrl(intent) {
      var requestLimit = Math.min(10, Math.max(6, this.minimumRecommendations * 3));
      return this.rootPath() +
        'recommendations/products?product_id=' + encodeURIComponent(this.dataset.productId) +
        '&intent=' + encodeURIComponent(intent) +
        '&limit=' + requestLimit +
        '&section_id=fbt-data';
    }

    fallbackUrls() {
      var root = this.rootPath();
      var urls = [];
      if (this.dataset.fallbackCollection) {
        urls.push(root + 'collections/' + encodeURIComponent(this.dataset.fallbackCollection) + '?section_id=fbt-data');
      }
      [1, 2, 3].forEach(function (page) {
        urls.push(root + 'collections/all?section_id=fbt-data&page=' + page);
      });
      return Array.from(new Set(urls));
    }

    existingProductIds() {
      return new Set(Array.from(this.querySelectorAll('.fbt__check[data-product-id]')).map(function (checkbox) {
        return String(checkbox.dataset.productId);
      }));
    }

    readKitContext() {
      var element = this.querySelector('[data-fbt-kit-context]');
      if (!element) return {};
      try {
        return JSON.parse(element.textContent.trim());
      } catch (error) {
        return {};
      }
    }

    normalizedText(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    significantTokens(value) {
      var ignored = new Set(['a', 'ao', 'aos', 'as', 'com', 'da', 'das', 'de', 'do', 'dos', 'e', 'em', 'na', 'nas', 'no', 'nos', 'o', 'os', 'para', 'por', 'sem', 'um', 'uma']);
      return this.normalizedText(value).split(/\s+/).filter(function (token) {
        return token && !ignored.has(token);
      });
    }

    componentTitleTokens(title) {
      var coreTitle = String(title || '').split(/\s(?:\||—|–)\s|:/)[0];
      return this.significantTokens(coreTitle);
    }

    containsTokenSequence(haystack, needle) {
      if (needle.length < 2 || haystack.length < needle.length) return false;
      for (var start = 0; start <= haystack.length - needle.length; start += 1) {
        var matches = true;
        for (var offset = 0; offset < needle.length; offset += 1) {
          if (haystack[start + offset] !== needle[offset]) {
            matches = false;
            break;
          }
        }
        if (matches) return true;
      }
      return false;
    }

    isKitTitle(title) {
      title = this.normalizedText(title);
      return /(^|\s)(kit|box|combo)(\s|$)|\bkit\s*\d+\b|\b\d+\s*livros?\b/.test(title);
    }

    isIncludedInCurrentKit(productId, title) {
      if (!this.currentProductIsKit) return false;
      if (this.kitComponentIds.has(String(productId || ''))) return true;
      return this.containsTokenSequence(this.kitDescriptionTokens, this.componentTitleTokens(title));
    }

    isBookTitle(title) {
      title = this.normalizedText(title);
      var isKit = this.isKitTitle(title);
      var isAccessory = /^(capa|caneca|jogo|marcador|marca pagina|sacola|necessaire)\b|\bcapa colchete\b|\bcapa para\b/.test(title);
      return !isKit && !isAccessory;
    }

    sanitizeExistingRows() {
      var seen = new Set([String(this.dataset.productId || '')]);
      this.querySelectorAll('.fbt__row:not(.fbt__row--main)').forEach(function (row) {
        var checkbox = row.querySelector('.fbt__check[data-product-id]');
        var titleElement = row.querySelector('.fbt__title');
        var productId = checkbox ? String(checkbox.dataset.productId || '') : '';
        var title = titleElement ? titleElement.textContent : '';
        var valid = checkbox && productId && !seen.has(productId) && this.isBookTitle(title) && !this.isIncludedInCurrentKit(productId, title);
        if (!valid) {
          row.remove();
          return;
        }
        seen.add(productId);
      }.bind(this));
    }

    isEligibleProduct(product) {
      var isCurrentProduct = String(product.productId || '') === String(this.dataset.productId || '');
      return product.available && product.id && product.productId && !isCurrentProduct && this.isBookTitle(product.title) && !this.isIncludedInCurrentKit(product.productId, product.title);
    }

    mergeUnique(target, incoming, seen) {
      incoming.forEach(function (product) {
        var productId = String(product.productId || '');
        if (!this.isEligibleProduct(product) || seen.has(productId)) return;
        seen.add(productId);
        target.push(product);
      }.bind(this));
    }

    loadRecommendations() {
      var currentCount = this.querySelectorAll('.fbt__check:not(:disabled)').length;
      var needed = Math.max(0, this.minimumRecommendations - currentCount);
      if (!needed) {
        this.initRows();
        return;
      }

      var products = [];
      var seen = this.existingProductIds();
      var primaryIntent = this.dataset.intent || 'complementary';
      var intents = primaryIntent === 'related' ? ['related', 'complementary'] : ['complementary', 'related'];
      var chain = Promise.resolve();

      intents.forEach(function (intent) {
        chain = chain.then(function () {
          if (products.length >= needed) return null;
          return this.fetchProducts(this.recommendationUrl(intent)).then(function (incoming) {
            this.mergeUnique(products, incoming, seen);
          }.bind(this));
        }.bind(this));
      }.bind(this));

      this.fallbackUrls().forEach(function (url) {
        chain = chain.then(function () {
          if (products.length >= needed) return null;
          return this.fetchProducts(url).then(function (incoming) {
            this.mergeUnique(products, incoming, seen);
          }.bind(this));
        }.bind(this));
      }.bind(this));

      chain
        .then(function () {
          this.appendCompanions(products.slice(0, needed));
          this.initRows();
          var finalCount = this.querySelectorAll('.fbt__check:not(:disabled)').length;
          this.classList.toggle('fbt--incomplete', finalCount < 3);
        }.bind(this))
        .catch(function () {
          this.initRows();
          this.classList.add('fbt--incomplete');
        }.bind(this));
    }

    appendCompanions(products) {
      var html = products.map(function (product) {
        var was = product.compare > product.price ? product.compare : 0;
        var discountPercent = was > product.price ? Math.round((was - product.price) * 100 / was) : 0;
        return (
          '<label class="fbt__row">' +
            '<input type="checkbox" class="fbt__check" data-variant-id="' + product.id + '" data-product-id="' + product.productId + '" data-price="' + product.price + '" checked>' +
            '<span class="fbt__thumb premium-figure is-visible">' +
              (product.image ? '<img src="' + esc(product.image) + '" alt="" loading="lazy">' : '') +
            '</span>' +
            '<span class="fbt__info">' +
              '<a href="' + esc(product.url) + '" class="fbt__title">' + esc(product.title) + '</a>' +
              '<span class="fbt__price">' +
                (discountPercent > 0 ? '<span class="fbt__discount">-' + discountPercent + '%</span>' : '') +
                '<span class="fbt__price-now">' + this.formatter.format(product.price / 100) + '</span>' +
                (was > 0 ? '<s class="fbt__price-was">' + this.formatter.format(was / 100) + '</s>' : '') +
              '</span>' +
              '<span class="fbt__combo-price" hidden aria-live="polite">' +
                '<span class="fbt__combo-price-label"></span>' +
                '<strong class="fbt__combo-price-value"></strong>' +
                '<span class="fbt__combo-price-discount"></span>' +
              '</span>' +
            '</span>' +
          '</label>'
        );
      }.bind(this)).join('');
      this.list.insertAdjacentHTML('beforeend', html);
    }

    initRows() {
      if (this.initialized) return;
      this.initialized = true;
      this.checks = Array.from(this.querySelectorAll('.fbt__check'));
      this.checks.forEach(function (checkbox) {
        checkbox.addEventListener('change', this.updateTotal.bind(this));
      }.bind(this));
      this.querySelectorAll('[data-fbt-tier]').forEach(function (tierButton) {
        tierButton.addEventListener('click', function () {
          this.selectTier(Number(tierButton.dataset.fbtTier) || 1);
        }.bind(this));
      }.bind(this));
      if (this.addBtn) this.addBtn.addEventListener('click', this.addSelected.bind(this));
      this.updateTotal();
    }

    selectTier(count) {
      var companions = this.checks.filter(function (checkbox) { return !checkbox.disabled; });
      var selectedCount = Math.min(Math.max(1, count), companions.length);
      companions.forEach(function (checkbox, index) {
        checkbox.checked = index < selectedCount;
      });
      this.updateTotal();
    }

    selectedItems() {
      return this.checks
        .filter(function (checkbox) { return checkbox.checked; })
        .map(function (checkbox) {
          return { id: Number(checkbox.dataset.variantId), price: Number(checkbox.dataset.price) };
        });
    }

    companionCount() {
      return this.checks.filter(function (checkbox) { return checkbox.checked && !checkbox.disabled; }).length;
    }

    activeOffer(companionCount) {
      if (companionCount < 1) return { count: 0, percent: 0, code: '' };
      if (this.discountMode === 'flat' && this.flatDiscount > 0 && this.flatCode) {
        return { count: 1, percent: this.flatDiscount, code: this.flatCode };
      }
      if (this.discountMode !== 'tiered') return { count: 0, percent: 0, code: '' };
      return this.tiers.reduce(function (active, tier) {
        if (tier.count <= companionCount && tier.percent > 0 && tier.code) return tier;
        return active;
      }, { count: 0, percent: 0, code: '' });
    }

    updateTierPresentation(companionCount, offer) {
      this.querySelectorAll('[data-fbt-tier]').forEach(function (tierElement) {
        var isActive = Number(tierElement.dataset.fbtTier) === offer.count && offer.percent > 0;
        tierElement.classList.toggle('is-active', isActive);
        tierElement.setAttribute('aria-pressed', String(isActive));
      });
      if (this.statusEl) this.statusEl.textContent = '';
    }

    updateRowPrices(offer) {
      this.querySelectorAll('.fbt__row').forEach(function (row) {
        var checkbox = row.querySelector('.fbt__check');
        var comboPrice = row.querySelector('.fbt__combo-price');
        if (!checkbox || !comboPrice) return;

        var shouldShow = checkbox.checked && offer.percent > 0;
        comboPrice.hidden = !shouldShow;
        if (!shouldShow) return;

        var basePrice = Number(checkbox.dataset.price) || 0;
        var discountedPrice = Math.round(basePrice * (100 - offer.percent) / 100);
        var label = comboPrice.querySelector('.fbt__combo-price-label');
        var value = comboPrice.querySelector('.fbt__combo-price-value');
        var discount = comboPrice.querySelector('.fbt__combo-price-discount');

        if (label) label.textContent = this.comboPriceLabel + ':';
        if (value) value.textContent = this.formatter.format(discountedPrice / 100);
        if (discount) discount.textContent = this.comboDiscountLabel.replace('[discount]', offer.percent);
      }.bind(this));
    }

    updateTotal() {
      var items = this.selectedItems();
      var companionCount = this.companionCount();
      var offer = this.activeOffer(companionCount);
      var cents = items.reduce(function (sum, item) { return sum + item.price; }, 0);
      var discountedCents = Math.round(cents * (100 - offer.percent) / 100);
      if (this.totalEl) this.totalEl.textContent = this.formatter.format(discountedCents / 100);
      if (this.addBtn) this.addBtn.disabled = companionCount === 0;
      if (this.buttonLabel) {
        this.buttonLabel.textContent = companionCount === 0
          ? this.emptyLabel
          : this.selectedLabel.replace('[count]', companionCount + 1);
      }
      if (this.buttonSeparator) this.buttonSeparator.hidden = companionCount === 0;
      if (this.totalEl) this.totalEl.hidden = companionCount === 0;
      this.updateRowPrices(offer);
      this.updateTierPresentation(companionCount, offer);
    }

    applyDiscountCode(code) {
      if (!code) return Promise.resolve(true);
      var url = this.rootPath() + 'discount/' + encodeURIComponent(code) + '?redirect=%2Fcart.js';
      return fetch(url, { credentials: 'same-origin', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) { return response.ok; })
        .catch(function () { return false; });
    }

    addSelected() {
      var items = this.selectedItems();
      if (!items.length) return;
      this.addBtn.classList.add('is-loading');

      var offer = this.activeOffer(this.companionCount());
      var cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      var sections = cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map(function (section) { return section.id; })
        : [];

      this.applyDiscountCode(offer.code)
        .then(function (discountApplied) {
          if (offer.code && !discountApplied) throw new Error('Não foi possível aplicar o código de desconto configurado.');
          return fetch((window.routes && window.routes.cart_add_url) || '/cart/add', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({
              items: items.map(function (item) { return { id: item.id, quantity: 1 }; }),
              sections: sections,
              sections_url: window.location.pathname,
            }),
          });
        })
        .then(function (response) { return response.json(); })
        .then(function (response) {
          if (response.status) throw new Error(response.description || response.message || 'Não foi possível adicionar o combo.');
          if (cart && typeof cart.renderContents === 'function') {
            cart.renderContents(response);
            if (typeof cart.open === 'function') cart.open();
          } else {
            window.location = (window.routes && window.routes.cart_url) || '/cart';
          }
        })
        .catch(function (error) {
          console.error('FBT:', error.message || error);
          if (this.statusEl) this.statusEl.textContent = 'Não foi possível adicionar o combo. Tente novamente.';
        }.bind(this))
        .finally(function () { this.addBtn.classList.remove('is-loading'); }.bind(this));
    }
  }

  if (!customElements.get('fbt-bundle')) customElements.define('fbt-bundle', FbtBundle);
})();
